import { TAG_NAME_MAX } from '@/function/work/tag.constants';
import { PALETTE_COLORS } from '@/theme/palette.colors';
import type { TypeTag, TypeTagColor } from '@/types/work/types.work.tag';
import type { TypeProject } from '@/types/work/types.work.project';
import type { TypeTask } from '@/types/work/types.work.task';

const TAG_COLORS: TypeTagColor[] = PALETTE_COLORS;

export const cleanTagName = (raw: string): string => raw.trim().replace(/\s+/g, ' ').slice(0, TAG_NAME_MAX);

export const sameTagName = (a: string, b: string): boolean =>
    a.localeCompare(b, 'it', { sensitivity: 'base' }) === 0;

export const findTagByName = (tags: TypeTag[], name: string): TypeTag | null =>
    tags.find((tag) => sameTagName(tag.name, cleanTagName(name))) ?? null;

export const tagsOf = (tags: TypeTag[], ids: string[]): TypeTag[] => ids
    .map((id) => tags.find((tag) => tag.id === id))
    .filter((tag): tag is TypeTag => Boolean(tag));

export const tagsInScope = (tags: TypeTag[], projectId: string | null): TypeTag[] =>
    tags.filter((tag) => !tag.projectId || tag.projectId === projectId);

export const countTags = (tasks: TypeTask[], projects: TypeProject[] = []): Map<string, number> => {
    const counts = new Map<string, number>();
    const add = (id: string) => counts.set(id, (counts.get(id) ?? 0) + 1);

    tasks.forEach((task) => task.tags.forEach(add));
    projects.forEach((project) => project.tags.forEach(add));

    return counts;
};

export const searchTags = (tags: TypeTag[], query: string): TypeTag[] => {
    const needle = cleanTagName(query).toLocaleLowerCase('it');
    if (!needle) return [...tags].sort((a, b) => a.name.localeCompare(b.name, 'it'));

    return tags
        .map((tag) => ({ tag, at: tag.name.toLocaleLowerCase('it').indexOf(needle) }))
        .filter((entry) => entry.at >= 0)
        .sort((a, b) => a.at - b.at || a.tag.name.localeCompare(b.tag.name, 'it'))
        .map((entry) => entry.tag);
};

export const canCreateTag = (tags: TypeTag[], query: string): boolean => {
    const clean = cleanTagName(query);
    return clean.length > 0 && !findTagByName(tags, clean);
};

export const nextTagColor = (tags: TypeTag[]): TypeTagColor => {
    const wheel = TAG_COLORS.filter((color) => color !== 'none');
    const used = new Map<TypeTagColor, number>(wheel.map((color) => [color, 0]));
    tags.forEach((tag) => {
        if (used.has(tag.color)) used.set(tag.color, (used.get(tag.color) ?? 0) + 1);
    });
    return wheel.reduce((best, color) => ((used.get(color) ?? 0) < (used.get(best) ?? 0) ? color : best), wheel[0]);
};
